// Per-course navigation gating logic. The course manifest declares a
// `navigationPolicy` ("linear" or "open"). Frontend pages call
// `isLessonUnlocked` to decide whether to render a lesson as a link or as a
// locked placeholder, and `firstIncompleteLesson` to point the student at
// where they should be working right now.
//
// The check is frontend-only — direct URL bypass is possible, but the
// Lesson page renders a "locked" notice in place of a locked lesson body,
// so a URL-only bypass yields the same "locked" UX as clicking from the
// sidebar.

import { buildFullSequence, lessonSequence, type CourseManifest, type LessonLocator } from "./course";
import type { ProgressRecord } from "./progress";

function lessonId(loc: { chapterFolder: string; lessonSlug: string }): string {
  return `${loc.chapterFolder}/${loc.lessonSlug}`;
}

/**
 * Returns true if the given lesson (or chapter-quiz pseudo-lesson) should be
 * navigable given the course's navigation policy and the student's progress.
 *
 * For "open" courses: always true.
 * For "linear" courses: true iff this is the first item in the full sequence
 * (which includes chapter-quizzes as pseudo-lessons after their chapter's real
 * lessons), OR every prior item in the sequence is marked complete.
 *
 * Chapter-quiz unlock rule: unlocked when all real lessons in its chapter are
 * done. Next-chapter lessons unlock when the chapter-quiz (if any) is done, or
 * when the prior chapter's last real lesson is done (no chapter-quiz case).
 */
export function isLessonUnlocked(
  manifest: CourseManifest,
  progress: ProgressRecord | null,
  chapterFolder: string,
  lessonSlug: string
): boolean {
  if (manifest.navigationPolicy !== "linear") return true;

  const seq = buildFullSequence(manifest);
  const idx = seq.findIndex(
    (s) => s.chapterFolder === chapterFolder && s.lessonSlug === lessonSlug
  );
  if (idx === -1) return false;
  if (idx === 0) return true;

  const completed = new Set(progress?.completedLessons ?? []);
  for (let i = 0; i < idx; i++) {
    const prior = seq[i]!;
    if (!completed.has(lessonId(prior))) return false;
  }
  return true;
}

/**
 * Returns the lesson the student should be working on right now — the first
 * lesson they haven't completed yet, in linear order. Falls back to the very
 * first lesson if they have no progress, or to the last lesson if they've
 * completed everything. Returns null only when the course has zero lessons.
 */
export function firstIncompleteLesson(
  manifest: CourseManifest,
  progress: ProgressRecord | null
): LessonLocator | null {
  const seq = lessonSequence(manifest);
  if (seq.length === 0) return null;
  const completed = new Set(progress?.completedLessons ?? []);
  for (const loc of seq) {
    if (!completed.has(lessonId(loc))) return loc;
  }
  return seq[seq.length - 1] ?? null;
}
